import { produce } from "immer";
import React from "react";

import { gradeLevelList, gradeLevelMeta } from "@examspark/common-meta";
import { Form, SelectInput, TextInput } from "@examspark/common-ui";
import { Course } from "@examspark/types";

import { useQueryGetAllCalendarsForCourse } from "../../../api/operations/calendar";
import {
  useQueryGetCourse,
  useQueryGetCoursesBySubject,
} from "../../../api/operations/course";
import FormLabel from "../../FormLabel";
import CourseCalendarSelect from "../CourseCalendarSelect";

import SubjectSelectInput from "./SubjectSelectInput";

export interface NewClassroomFormData {
  course?: string;
  grade?: string;
  subject?: string;
  calendar?: {
    id: string;
    sk: string;
  };
  name: string;
  room: string;
  sectionNumber: string;
}

const NewClassroomForm = (props: {
  /** Do not show subject, grade, and course select inputs. */
  hideCourseInput?: boolean;
  hideGradeInput?: boolean;
  hideSubjectInput?: boolean;
  allowedSubjects?: string[];
  onChange: (value: NewClassroomFormData) => void;
  value: NewClassroomFormData;
}) => {
  const { data: courses } = useQueryGetCoursesBySubject(props.value.subject);

  const courseResult = useQueryGetCourse(props.value.course);
  const calendarsQuery = useQueryGetAllCalendarsForCourse(courseResult.data);

  return (
    <div>
      <Form.FieldList style={{ gap: "1.5rem" }}>
        {!props.hideSubjectInput && (
          <div data-cy="new-classroom-form-field-subject">
            <FormLabel label="Which subject are you teaching in this classroom? *">
              <SubjectSelectInput
                autoFocus
                value={props.value.subject}
                allowedSubjects={props.allowedSubjects}
                onChange={(e: any) => {
                  props.onChange(
                    produce(props.value, (draft) => {
                      draft.subject = e?.value;
                    }),
                  );
                }}
              />
            </FormLabel>
          </div>
        )}
        {!props.hideGradeInput && (
          <div data-cy="new-classroom-form-field-grade">
            <FormLabel label="What is the grade level of this classroom? *">
              <SelectInput
                // disabled={!setupCourse}
                value={props.value.grade}
                onChange={(e) =>
                  props.onChange(
                    produce(props.value, (draft) => {
                      draft.grade = e?.value;
                    }),
                  )
                }
                options={(gradeLevelList ?? []).map((grade) => ({
                  label: gradeLevelMeta[grade].label,
                  value: grade,
                }))}
                menuPortalTarget={
                  document.querySelector("#root") as HTMLElement
                }
              />
            </FormLabel>
          </div>
        )}
        {!props.hideCourseInput && (
          <div data-cy="new-classroom-form-field-course">
            <FormLabel label="Which course are you teaching in this classroom? *">
              <SelectInput
                value={props.value.course}
                onChange={(e) =>
                  props.onChange(
                    produce(props.value, (draft) => {
                      draft.course = e?.value;
                    }),
                  )
                }
                menuPortalTarget={
                  document.querySelector("#root") as HTMLElement
                }
                options={(courses ?? [])
                  .filter((course: Course) => {
                    if (
                      props.value.subject &&
                      course.subject?.includes(props.value.subject)
                    ) {
                      return true;
                    }

                    return false;
                  })
                  .sort((a: Course, b: Course) => {
                    const aSeq = a.sequence ?? "";
                    const bSeq = b.sequence ?? "";

                    return aSeq?.localeCompare(bSeq);
                  })
                  .map((course: Course) => ({
                    label: course.name ?? "",
                    value: course.id,
                  }))}
              />
            </FormLabel>
          </div>
        )}
        {calendarsQuery.data && calendarsQuery.data.length > 0 && (
          <div data-cy="new-classroom-form-field-calendar">
            <FormLabel label="Which calendar would you like to use for this classroom? *">
              <CourseCalendarSelect
                value={props.value.calendar}
                onChange={(calendar) =>
                  props.onChange(
                    produce(props.value, (draft) => {
                      draft.calendar = calendar;
                    }),
                  )
                }
                course={courseResult.data}
              />
            </FormLabel>
          </div>
        )}
        <FormLabel label="What is the name of this classroom?">
          <TextInput
            data-cy="new-classroom-form-field-name"
            type="text"
            value={props.value.name}
            onChange={(e) =>
              props.onChange(
                produce(props.value, (draft) => {
                  draft.name = e.target.value;
                }),
              )
            }
          />
        </FormLabel>
        <FormLabel label="What is the room number of this classroom?">
          <TextInput
            data-cy="new-classroom-form-field-room-number"
            type="text"
            value={props.value.room}
            onChange={(e) =>
              props.onChange(
                produce(props.value, (draft) => {
                  draft.room = e.target.value;
                }),
              )
            }
          />
        </FormLabel>
        <FormLabel label="What is the section number of this classroom?">
          <TextInput
            data-cy="new-classroom-form-field-section-number"
            type="text"
            value={props.value.sectionNumber}
            onChange={(e) =>
              props.onChange(
                produce(props.value, (draft) => {
                  draft.sectionNumber = e.target.value;
                }),
              )
            }
          />
        </FormLabel>
      </Form.FieldList>
    </div>
  );
};

export default NewClassroomForm;
